import Color from "./color";
import amazonConnectAgentStates from "./AmazonConnectAgentState";

export default class StateButtonController {
  firmwareVersionErrorSent = false;
  stateButtonColor = undefined;
  activeDevice;

  constructor(logger, activeDevice) {
    this.logger = logger;
    this.activeDevice = activeDevice;
  }

  async init() {
    try {
      // Checks if active device allow button take over / lighting etc.: then check the agent state
      const stateButton = this.getStateButton();
      if (stateButton !== null) {
        this.logger.debug("Setting up state button");
        await jabra.setMmiFocus(stateButton, true);
      }
    } catch (e) {
      this.logger.error("Following error occurred setting up the device and the button light: " + e.message);
    }
  }

  reset() {
    this.stateButtonColor = undefined;
  }

  async setActiveDevice(activeDevice) {
    this.activeDevice = activeDevice;
    await this.init();
  }

  async isStateButtonPress(event) {
    const stateButton = this.getStateButton();
    if (stateButton !== null && event.data.activeDevice) {
      let buttonType = event.data.type;
      let actionType = event.data.action;
      if (buttonType === stateButton && actionType === jabra.RemoteMmiActionInput.MMI_ACTION_TAP) {
        return true;
      }
    }

    return false;
  }

  /**
   * Changes the color of the state button on the headset controller
   * depending on the state of the Amazon Connect agent state.
   * @param {String} state
   */
  alignStateButtonWithAgentState(state) {
    try {
      const color = this.getStateColor(state);
      this.setColorOfButton(color);      
    } catch (error) {
      this.logger.error("An error occurred when changing color on the device button. Error message: ", error.message);
    }
  }

  getStateColor(state) {
    switch (state) {
      case amazonConnectAgentStates.AFTER_CALL_WORK:
        return Color.blue
      case amazonConnectAgentStates.PENDING_BUSY:
        return Color.yellow;        
      case amazonConnectAgentStates.MISSED_CALL_AGENT:
        return Color.blue;        
      case amazonConnectAgentStates.INIT:
        return Color.blue;        
      case amazonConnectAgentStates.OFFLINE:
        return Color.red;        
      case amazonConnectAgentStates.BUSY:
        return Color.red;        
      case amazonConnectAgentStates.FAILED_CONNECT_AGENT:
        return Color.red;        
      case amazonConnectAgentStates.AVAILABLE:
        return Color.green;        
      default:
        return Color.yellow
    }
  }

  /**
   * Changes the color of the state button if the button is not already that color
   * @param {String(hex color code)} colorToChangeTo
   */
   setColorOfButton(colorToChangeTo, hardChange = false) {
    const stateButton = this.getStateButton();
    if (stateButton === null) {
      return;
    }

    if (this.stateButtonColor !== colorToChangeTo || hardChange) {
      try {
        jabra.setRemoteMmiLightAction(stateButton, colorToChangeTo.rgb, jabra.RemoteMmiSequence.MMI_LED_SEQUENCE_ON);
        this.logger.debug(
          `Changed color of state button from ${this.stateButtonColor?.name ?? "undefined"} to ${colorToChangeTo.name}.`
        );
      } catch (error) {
        this.logger.error(`Failed to set the color of the state button to ${colorToChangeTo.name}.`, error);
      }

      this.stateButtonColor = colorToChangeTo;
    }
  }

  getStateButton() {
    if (!this.activeDevice) {
      return null;
    }

    if (this.isEngage50WithRightVersion(this.activeDevice)) {
      return jabra.RemoteMmiType.MMI_TYPE_DOT4;
    }

    if (
      this.activeDevice.deviceName.startsWith("Jabra Engage 40") ||
      this.activeDevice.deviceName.startsWith("Jabra Engage 50 II")
    ) {
      return jabra.RemoteMmiType.MMI_TYPE_DOT3;
    }

    return null;
  }

  /**
   * Should be : Jabra Engage 50 AND have remoteMMIv2 feature
   * Should NOT be: version 2.3.1 -> throw error
   * @param {import('../ts/jabra.browser.integration-3.0.js').DeviceInfo} device
   */
  isEngage50WithRightVersion(device) {
    let blacklistedFWVersions = ["2.3.1", "2.4.0"];
    if (device?.deviceName !== "Jabra Engage 50") {
      return false;
    }

    if (!blacklistedFWVersions.includes(device?.firmwareVersion)) {
      return true;
    }

    if (!this.firmwareVersionErrorSent) {
      this.firmwareVersionErrorSent = true;
      throw Error(
        `The setup of the 4-dot button on your Jabra Engage 50 controller failed due to a known firmware issue with FW version ${device?.firmwareVersion}. Please use Jabra Direct to check for an update to your firmware or contact Jabra support.`
      );
    }

    return false;
  }
}
